/**
 * Single query-key registry (ARCHITECTURE-LAYERS.md §4.2).
 *
 * Replaces the five per-feature key objects (`chatKeys`, `strategyKeys`,
 * `settingsKeys`, `portfolioKeys`, `orderKeys`, `notificationKeys`, and the ad
 * hoc `queryKeys` in `lib/query.ts`) that let the same logical cache entry
 * drift into slightly different key strings. Two real bugs this collapses
 * (the only intentional key-string changes — everything else keeps its
 * existing string):
 *  - `settingsKeys.accounts` was `['accounts']` while `portfolioKeys.accounts()`
 *    was `['accounts','list']` — syncing accounts never refreshed the
 *    dashboard's account-dependent views. Both now read/write
 *    `keys.accounts.list()`.
 *  - "strategies list" was defined three times (`strategyKeys.list`,
 *    `chatKeys.strategies`, `portfolioKeys.strategies()`); all three now
 *    resolve to `keys.strategies.list(status)` (unfiltered callers get the
 *    `'all'` bucket, same as the strategies feature's own filtered reads).
 */
import type { OrderStatusGroup, PortfolioPeriod, StrategyStatus } from "@richrich/contracts";

export const keys = {
  auth: {
    session: () => ["auth", "session"] as const,
  },
  settings: {
    root: () => ["settings"] as const,
    credentials: () => ["settings", "credentials"] as const,
    aiAliases: () => ["settings", "ai-aliases"] as const,
    aiModels: () => ["settings", "ai-models"] as const,
    agentTools: () => ["settings", "agent-tools"] as const,
  },
  accounts: {
    list: () => ["accounts", "list"] as const, // ← ONE definition (fixes the sync bug)
  },
  strategies: {
    all: ["strategies"] as const,
    listAll: ["strategies", "list"] as const, // every status bucket — broad invalidation
    list: (status?: StrategyStatus) => ["strategies", "list", status ?? "all"] as const,
    detail: (id: string) => ["strategies", "detail", id] as const,
    pnl: (id: string) => ["strategies", "pnl", id] as const,
    portfolio: (id: string) => ["strategies", "portfolio", id] as const,
    runs: (id: string) => ["strategies", "runs", id] as const,
    decisions: (id: string, runId: string) =>
      ["strategies", "runs", id, runId, "decisions"] as const,
    presets: () => ["strategies", "presets"] as const,
    backtests: {
      all: (strategyId: string) => ["strategies", "detail", strategyId, "backtests"] as const,
      list: (strategyId: string) =>
        ["strategies", "detail", strategyId, "backtests", "list"] as const,
      detail: (strategyId: string, backtestId: string) =>
        ["strategies", "detail", strategyId, "backtests", "detail", backtestId] as const,
    },
  },
  chat: {
    sessions: () => ["chat", "sessions"] as const,
    session: (id: string) => ["chat", "session", id] as const,
  },
  portfolio: {
    all: ["portfolio"] as const,
    summary: () => ["portfolio", "summary"] as const,
    timeseries: (period: PortfolioPeriod) => ["portfolio", "timeseries", period] as const,
    decomposition: (from: number, to: number) => ["portfolio", "decomposition", from, to] as const,
    allocation: () => ["portfolio", "allocation"] as const,
    holdings: () => ["portfolio", "holdings"] as const,
    cashflows: () => ["portfolio", "cashflows"] as const,
  },
  market: {
    all: ["market"] as const,
    indices: () => ["market", "indices"] as const,
    sectors: () => ["market", "sectors"] as const,
    stocks: () => ["market", "stocks"] as const,
    fgi: () => ["market", "fgi"] as const,
    fgiHistory: (days: number) => ["market", "fgi", "history", days] as const,
    news: () => ["market", "news"] as const,
  },
  orders: {
    all: ["orders"] as const,
    list: (filter: "all" | OrderStatusGroup) => ["orders", "list", filter] as const,
  },
  notifications: {
    list: () => ["notifications", "list"] as const,
    policy: () => ["notifications", "policy"] as const,
  },
  approvals: {
    all: ["approvals"] as const, // broad invalidation prefix — matches `pending()` too
    pending: () => ["approvals", "pending"] as const,
  },
} as const;
